import React, { useEffect, useState } from 'react';
import './CustomerDescription.css';
import FilterDropdown from "../TeamPortalUtil/FilterDropdown";
import TeamHeader from '../TeamHeader/TeamHeader';
import { Link, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../../Auth';

// Toast imports
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Icons
import { FaMagnifyingGlass } from "react-icons/fa6";
import { LuPencil } from "react-icons/lu";
import { MdCloudUpload } from "react-icons/md";
import { CgTrash } from "react-icons/cg";
import { FaPlus } from "react-icons/fa";

//Assets
import fnnLogo from '../../img/FNNLogoWithBlackFont-06.png'
// ----------------- Interfaces -----------------
interface OrgUser {
    id: number;
    user_id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
}

interface Permission {
    App: string;
    Category: string;
}

interface MarketingOrg {
    id: number;
    ORG_NAME: string;
    ORG_LOGO: string;
    ORG_PLAN: string;
    ORG_INDUSTRY: string;
    ORG_PERMISSIONS: Permission[];   // ⬅ now array of objects
    ORG_LOCATIONS: string[];
    ORG_POC?: string | null;
    ORG_ONBOARDING?: string | null;
    users: OrgUser[];
}

// ----------------- Component -----------------
const CustomerDescription: React.FC = () => {

    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to the top when the component mounts
    }, []);


    function displayNotification(successOrFail: boolean, message: string) {
        if (successOrFail) {
            toast.success(message, {
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#e7ecf1ff',
                    color: 'black',
                    fontSize: '16px',
                }
            });
        }
        else {
            toast.error(message, {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#e7ecf1ff',
                    color: 'black',
                    fontSize: '16px',
                }
            });
        }
    }

    // Checking the user's permission to see if he can or cannot edit the orgs
    const { isAuthenticated, getPermissions } = useAuth();
    const [permissionGroups, setPermissionGroups] = useState<{ [key: string]: any }>({});

    useEffect(() => {
        const fetchPermissions = async () => {
            const permissions = await getPermissions(); // Await the promise
            setPermissionGroups(permissions || {});
        };

        if (isAuthenticated) {
            fetchPermissions();
        }
    }, [isAuthenticated, getPermissions]);

    const [orgs, setOrgs] = useState<MarketingOrg[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedOrg, setSelectedOrg] = useState<string | null>(null);

    // Search & filters
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
    const [selectedRegions, setSelectedRegions] = useState<string[]>([]);


    // Option to navigate to MainMarketing.tsx with that region selected
    const navigate = useNavigate();

    const handleRegionClick = (region: string) => {
        navigate("/marketing/main", { state: { preselectedRegion: region } });
    };


    // ---------------------------------- Util functions ----------------------------------

    const planOrder = ["Starter", "Pro", "Enterprise"];

    const filteredOrgs = orgs
        .filter(org => {
            const matchesSearch = org.ORG_NAME.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesPlan = selectedPlans.length > 0
                ? selectedPlans.includes(org.ORG_PLAN)
                : true;

            const matchesRegion = selectedRegions.length > 0
                ? org.ORG_LOCATIONS.some(loc => selectedRegions.includes(loc))
                : true;

            return matchesSearch && matchesPlan && matchesRegion;
        })
        .sort((a, b) => planOrder.indexOf(a.ORG_PLAN) - planOrder.indexOf(b.ORG_PLAN));


    const formatRegionName = (name: string) =>
        name.replace(/^detectors_/, '')
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin", // include cookies in the request
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    // Popup

    const emptyOrgTemplate: MarketingOrg = {
        id: 0, // dummy
        ORG_NAME: "",
        ORG_LOGO: "",
        ORG_PLAN: "Starter",
        ORG_INDUSTRY: "Wildfire",
        ORG_PERMISSIONS: [],
        ORG_LOCATIONS: [],
        ORG_POC: null,
        ORG_ONBOARDING: null,
        users: []
    };


    const [activeSection, setActiveSection] = useState("");
    const [showDeleteOrgPopup, setShowDeleteOrgPopup] = useState(false);
    const [orgBeingEdited, setOrgBeingEdited] = useState<MarketingOrg | null>(null);
    const [editForm, setEditForm] = useState<MarketingOrg | null>(null);

    // State for POC input and focus
    const [pocSearch, setPocSearch] = useState("");
    const [pocFocused, setPocFocused] = useState(false);

    // Update initial value whenever editForm changes
    useEffect(() => {
        if (editForm && Array.isArray(editForm.users) && editForm.users.length > 0) {
            const pocUser = editForm.users.find(
                u => u.id.toString() === String(editForm.ORG_POC ?? "")
            );

            setPocSearch(pocUser ? `${pocUser.first_name} ${pocUser.last_name}` : "");
        } else {
            setPocSearch("");
        }
    }, [editForm]);


    const handleEditChange = (field: keyof MarketingOrg, value: any) => {
        if (!editForm) return;
        setEditForm((prev) => prev ? { ...prev, [field]: value } : prev);
    };

    const saveOrg = async () => {
        if (!editForm) return;

        const isCreate = activeSection === "CreateOrg";

        const formData = new FormData();
        formData.append("ORG_NAME", editForm.ORG_NAME);
        formData.append("ORG_PLAN", editForm.ORG_PLAN);
        formData.append("ORG_INDUSTRY", editForm.ORG_INDUSTRY);

        editForm.ORG_PERMISSIONS.forEach(p =>
            formData.append("ORG_PERMISSIONS_ids", p.App)
        );

        editForm.ORG_LOCATIONS.forEach(l =>
            formData.append("ORG_LOCATIONS", l)
        );

        formData.append("ORG_POC", editForm.ORG_POC ?? "");
        formData.append("ORG_ONBOARDING", editForm.ORG_ONBOARDING ?? "");

        if (selectedFile) {
            formData.append("ORG_LOGO", selectedFile);
        }

        const url = isCreate
            ? "/portal/api/marketing_orgs/"
            : `/portal/api/marketing_orgs/${orgBeingEdited?.id}/`;

        const method = isCreate ? "POST" : "PUT";

        try {
            const csrfToken = await fetchCsrfToken();
            const res = await fetch(url, {
                method,
                credentials: "same-origin",
                headers: { "X-CSRFToken": csrfToken || "" },
                body: formData,
            });

            if (!res.ok) {
                const errText = await res.text();
                console.error(errText);

                displayNotification(
                    false,
                    isCreate
                        ? "Failed to create customer."
                        : "Failed to update customer."
                );
                return;
            }

            const savedOrg = await res.json();

            setOrgs(prev =>
                isCreate
                    ? [...prev, savedOrg]
                    : prev.map(o => o.id === savedOrg.id ? savedOrg : o)
            );

            displayNotification(
                true,
                isCreate
                    ? "Customer successfully created."
                    : "Customer successfully updated."
            );

            setActiveSection("");
            setEditForm(null);
            setOrgBeingEdited(null);
            setSelectedFile(null);

        } catch (err) {
            console.error("Error saving org", err);
            displayNotification(false, "Unexpected error while saving customer.");
        }
    };


    const handleDeleteOrg = async () => {
        if (!orgBeingEdited) return;

        try {
            const csrfToken = await fetchCsrfToken();
            const res = await fetch(
                `/portal/api/marketing_orgs/${orgBeingEdited.id}/`,
                {
                    method: "DELETE",
                    credentials: "same-origin",
                    headers: {
                        "X-CSRFToken": csrfToken || "",
                    },
                }
            );

            if (!res.ok) {
                const errText = await res.text();
                console.error(errText);

                displayNotification(false, "Failed to delete customer.");
                return;
            }

            setOrgs(prev => prev.filter(o => o.id !== orgBeingEdited.id));

            displayNotification(true, "Customer successfully deleted.");

            setActiveSection("");
            setEditForm(null);
            setOrgBeingEdited(null);
            setSelectedFile(null);

        } catch (err) {
            console.error("Error deleting organization", err);
            displayNotification(false, "Unexpected error while deleting customer.");
        }
    };

    // Fetching permissions and locations
    const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
    const [availableLocations, setAvailableLocations] = useState<string[]>([]);

    useEffect(() => {
        const fetchMeta = async () => {
            try {
                const [permRes, locRes] = await Promise.all([
                    fetch("/portal/api/possible_permissions/", { credentials: "same-origin" }),
                    fetch("/apidet/org_locations/", { credentials: "same-origin" }),
                ]);

                if (permRes.ok) {
                    const permData = await permRes.json();
                    setAvailablePermissions(permData.organization_permissions);
                }
                if (locRes.ok) {
                    const locData = await locRes.json();
                    setAvailableLocations(locData.org_locations);
                }
            } catch (err) {
                console.error("Error fetching permissions/locations", err);
            }
        };
        fetchMeta();
    }, []);

    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const response = await fetch("/portal/api/marketing_orgs/", {
                    method: "GET",
                    credentials: "same-origin", // include cookies/session
                });

                if (response.ok) {
                    const data: MarketingOrg[] = await response.json();
                    setOrgs(data);
                } else {
                    console.error("Failed to fetch organizations", response.status);
                }
            } catch (error) {
                console.error("Error fetching organizations", error);
            } finally {
                setLoading(false);
            }
        };

        fetchOrgs();
    }, []);

    // When user comes from clicking the main page's popup
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const orgId = params.get("org");

        if (orgId && orgs.length > 0) {
            const foundOrg = orgs.find(o => o.id === Number(orgId));

            if (foundOrg) {
                setSelectedOrg(foundOrg.ORG_NAME);

                setTimeout(() => {
                    const element = document.querySelector(
                        `.customer-org-expanded-wrapper.open`
                    );

                    element?.scrollIntoView({
                        behavior: "smooth",
                        block: "start"
                    });

                    // ✅ Remove query param WITHOUT reload
                    const cleanUrl = location.pathname;
                    window.history.replaceState({}, '', cleanUrl);

                }, 200);
            }
        }
    }, [location.search, orgs]);



    // ---------------------------------- Util functions ----------------------------------

    if (loading) {
        return <div className="customer-directory-body"><p>Loading...</p></div>;
    }

    const toggleOrg = (orgName: string) => {
        setSelectedOrg(prev => (prev === orgName ? null : orgName));
    };

    return (
        <div className='customer-directory-body'>
            <TeamHeader title="Marketing" />

            <ToastContainer />

            <h1 className='cutomer-title'>Customer Directory</h1>

            <div className='customer-search-and-sort customer-search-with-add'>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingRight: '1rem' }}>
                    <div className="customer-search-wrapper">
                        <FaMagnifyingGlass className="search-icon" />
                        <input
                            className="customer-search"
                            type="text"
                            name="Search_Field"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <FilterDropdown
                        sections={[
                            {
                                key: "regions",
                                title: "Region",
                                options: availableLocations,
                                formatLabel: (region) => formatRegionName(region),
                            },
                            {
                                key: "plans",
                                title: "Plans",
                                options: planOrder,
                            },
                        ]}
                        onChange={(filters) => {
                            setSelectedRegions(filters.regions || []);
                            setSelectedPlans(filters.plans || []);
                        }}
                    />

                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div className="filter-wrapper add-org-customer" style={{ position: 'relative' }}>
                        <button
                            onClick={() => {
                                setOrgBeingEdited(null);
                                setEditForm({ ...emptyOrgTemplate });
                                setSelectedFile(null);
                                setActiveSection("CreateOrg");
                            }}
                        >
                            <FaPlus /> Add Customer
                        </button>

                    </div>
                </div>

            </div>


            {/* Organization list */}
            <div className='customer-org-list'>
                {filteredOrgs.map((org) => (
                    <div key={org.ORG_NAME} style={{ width: '100%' }}>

                        <div
                            className='customer-org'
                            onClick={() => toggleOrg(org.ORG_NAME)}>

                            <div className='customer-org-inner'>
                                <img src={org.ORG_LOGO ? org.ORG_LOGO : fnnLogo} alt={org.ORG_NAME} />
                                <h1>{org.ORG_NAME}</h1>
                            </div>

                            <h1
                                className={`customer-plan ${org.ORG_PLAN.toLowerCase()}`}
                            >
                                {org.ORG_PLAN}
                            </h1>
                        </div>

                        {/* Expanded details */}
                        <div
                            className={`customer-org-expanded-wrapper ${selectedOrg === org.ORG_NAME ? "open" : ""}`}
                        >
                            <div className='customer-org-expanded'>
                                <div className='customer-org-expanded-left'>
                                    <div>
                                        <p><strong>Plan</strong></p>
                                        <p className={`customer-plan ${org.ORG_PLAN.toLowerCase()} plan-small`}>{org.ORG_PLAN}</p>
                                    </div>

                                    <div>
                                        <p><strong>Industry</strong></p>
                                        <p>{org.ORG_INDUSTRY}</p>
                                    </div>

                                    <div>
                                        <p><strong>On-boarding</strong></p>

                                        <p>
                                            {org.ORG_ONBOARDING
                                                ? format(parseISO(org.ORG_ONBOARDING), 'MM/dd/yyyy')
                                                : "Unset"}
                                        </p>

                                    </div>

                                    <div>
                                        <p><strong>Regions</strong></p>
                                        <p>
                                            {org.ORG_LOCATIONS.length > 0 ? (
                                                org.ORG_LOCATIONS.map((location, index) => (
                                                    <React.Fragment key={index}>
                                                        <span
                                                            className="customer-region-class"
                                                            onClick={(e) => {
                                                                e.stopPropagation(); // avoid toggling the org
                                                                handleRegionClick(location);
                                                            }}
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            {formatRegionName(location)}
                                                        </span>
                                                        {index < org.ORG_LOCATIONS.length - 1 && ", "}
                                                    </React.Fragment>
                                                ))
                                            ) : (
                                                <span>Unset</span>
                                            )}
                                        </p>
                                    </div>



                                    <div>
                                        <p><strong>Active Products</strong></p>
                                        <p>
                                            {org.ORG_PERMISSIONS.filter(p => p.Category === "Product").length > 0 ? (

                                                org.ORG_PERMISSIONS.filter(p => p.Category === "Product").map((p, index, arr) => (
                                                    <React.Fragment key={p.App}>
                                                        <span
                                                            className="customer-region-class"
                                                            style={{ cursor: "pointer" }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/marketing/products/${p.App.toLowerCase().replace(/\s+/g, "_")}`, {
                                                                    state: {
                                                                        preselectedOrgId: org.id,   // org to highlight
                                                                        preselectedProduct: p.App,  // product clicked
                                                                    }
                                                                });

                                                            }}
                                                        >
                                                            {p.App}
                                                        </span>
                                                        {index < arr.length - 1 && ", "}
                                                    </React.Fragment>
                                                ))

                                            ) : (
                                                <span>Unset</span>
                                            )}
                                        </p>
                                    </div>

                                    <div>
                                        <p><strong>Default Permissions</strong></p>
                                        <p>
                                            {org.ORG_PERMISSIONS.filter(p => p.Category === "Permission").length > 0 ? (
                                                org.ORG_PERMISSIONS.filter(p => p.Category === "Permission").map((p, index, arr) => (
                                                    <React.Fragment key={p.App}>
                                                        <span>{p.App}</span>
                                                        {index < arr.length - 1 && ", "}
                                                    </React.Fragment>
                                                ))
                                            ) : (
                                                <span>Unset</span>
                                            )}
                                        </p>
                                    </div>


                                </div>

                                <div className='customer-org-expanded-right'>

                                    <div>
                                        <div className='customer-pencil-and-poc'>
                                            <strong>Point of Contact</strong>{" "}

                                            <div className='customer-pencil-and-trash'>
                                                {permissionGroups.Admin &&
                                                    <CgTrash
                                                        className="customer-trash-icon"
                                                        title="Delete customer"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOrgBeingEdited(org);
                                                            setShowDeleteOrgPopup(true);
                                                        }}
                                                    />

                                                }

                                                {permissionGroups.Admin &&
                                                    <LuPencil
                                                        className="customer-org-edit-icon"
                                                        title="Edit customer"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOrgBeingEdited(org);
                                                            setEditForm({ ...org }); // copy org into editable state
                                                            setActiveSection("EditOrg");
                                                        }}
                                                    />
                                                }
                                            </div>
                                        </div>

                                        <p className={org.ORG_POC ? "customer-region-class" : ""}>
                                            {org.ORG_POC
                                                ? (() => {
                                                    const pocUser = org.users.find(user => user.id === Number(org.ORG_POC));
                                                    return pocUser ? `${pocUser.first_name} ${pocUser.last_name}` : "Unset";
                                                })()
                                                : "Unset"
                                            }
                                        </p>


                                    </div>

                                    <div>
                                        <p><strong>Members</strong>{" "}</p>
                                        <div className="customer-org-expanded-right-members">
                                            {org.users.length > 0 ? (
                                                org.users.map((user, index) => (
                                                    <button key={user.id}>
                                                        <strong>{index + 1}.</strong> {user.first_name} {user.last_name}
                                                    </button>
                                                ))
                                            ) : (
                                                <span>None</span>
                                            )}
                                        </div>

                                    </div>



                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {(activeSection === "EditOrg" || activeSection === "CreateOrg") && editForm && (

                <div className="active-products-popup-overlay">
                    <div className="active-products-popup-content">

                        <h2 className="edit-org-popup-header">
                            {activeSection === "CreateOrg" ? "Add Customer" : "Edit Customer"}
                        </h2>

                        <p>
                            {activeSection === "CreateOrg"
                                ? "Create a new customer."
                                : "Modify an existing customer."}
                        </p>

                        <div className="edit-org-form">


                            <div className="customer-edit-logo-name full-width-section">
                                {/* Logo */}
                                <div
                                    className="customer-edit-img"
                                    onClick={() => document.getElementById("org-logo-input")?.click()}
                                >
                                    {selectedFile ? (
                                        <img
                                            src={URL.createObjectURL(selectedFile)}
                                            alt="Selected Logo"
                                            className="customer-edit-img-img"
                                        />
                                    ) : editForm.ORG_LOGO ? (
                                        <img
                                            src={editForm.ORG_LOGO}
                                            alt="Current Logo"
                                            className="customer-edit-img-img"
                                        />
                                    ) : (
                                        <div className="customer-edit-img-placeholder">
                                            <MdCloudUpload size={30} />
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        id="org-logo-input"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files[0]) {
                                                setSelectedFile(e.target.files[0]);
                                            }
                                        }}
                                    />
                                </div>

                                {/* Company Name */}
                                <label className="customer-edit-org-name">
                                    <strong>Name:</strong>
                                    <input
                                        type="text"
                                        value={editForm.ORG_NAME}
                                        onChange={(e) => handleEditChange("ORG_NAME", e.target.value)}
                                    />
                                </label>
                            </div>


                            {/* ORG_PLAN */}
                            <label>
                                <strong>Plan:</strong>
                                <select
                                    value={editForm.ORG_PLAN}
                                    onChange={(e) => handleEditChange("ORG_PLAN", e.target.value)}
                                >
                                    <option value="Starter">Starter</option>
                                    <option value="Pro">Pro</option>
                                    <option value="Enterprise">Enterprise</option>
                                </select>
                            </label>

                            {/* ORG_INDUSTRY */}
                            <label>
                                <strong>Industry:</strong>
                                <select
                                    value={editForm.ORG_INDUSTRY}
                                    onChange={(e) => handleEditChange("ORG_INDUSTRY", e.target.value)}
                                >
                                    <option value="Wildfire">Wildfire</option>
                                    <option value="Utility">Utility</option>
                                    <option value="Other">Other</option>
                                </select>
                            </label>

                            {/* ORG_ONBOARDING */}
                            <label>
                                <strong>On-boarding Date:</strong>
                                <input
                                    type="date"
                                    value={editForm.ORG_ONBOARDING || ""}
                                    onChange={(e) => handleEditChange("ORG_ONBOARDING", e.target.value)}
                                />
                            </label>

                            {/* ORG_POC */}
                            <label style={{ position: "relative" }}>
                                <strong>Point of Contact:</strong>
                                <input
                                    type="text"
                                    value={pocSearch}
                                    placeholder="Type to search members..."
                                    onChange={(e) => {
                                        setPocSearch(e.target.value);
                                        setPocFocused(true); // open dropdown while typing
                                    }}
                                    onFocus={() => setPocFocused(true)}
                                    onBlur={() => setTimeout(() => setPocFocused(false), 150)} // small delay to allow click
                                />
                                {(pocFocused && editForm?.users.length > 0) && (
                                    <div className="autocomplete-list">
                                        {editForm.users
                                            .filter(user =>
                                                `${user.first_name} ${user.last_name}`.toLowerCase().includes(pocSearch.toLowerCase())
                                            )
                                            .map(user => (
                                                <div
                                                    key={user.id}
                                                    className="autocomplete-item"
                                                    onClick={() => {
                                                        handleEditChange("ORG_POC", user.id.toString()); // use id
                                                        setPocSearch(`${user.first_name} ${user.last_name}`);
                                                        setPocFocused(false);
                                                    }}
                                                >
                                                    {user.first_name} {user.last_name} ({user.email})
                                                </div>
                                            ))}

                                    </div>
                                )}
                            </label>


                            {/* ORG_PERMISSIONS split into Products + Permissions */}
                            <label className="full-width-section">
                                <strong>Products:</strong>
                                <div className="multi-select-container">
                                    {/* Available Products */}
                                    <div className="multi-select-list">
                                        <strong>Available</strong>
                                        {availablePermissions
                                            .filter(p => p.Category === "Product" && !editForm.ORG_PERMISSIONS.some(sel => sel.App === p.App))
                                            .map(p => (
                                                <div
                                                    key={p.App}
                                                    className="multi-select-item"
                                                    onClick={() =>
                                                        handleEditChange("ORG_PERMISSIONS", [...editForm.ORG_PERMISSIONS, p])
                                                    }
                                                >
                                                    {p.App} +
                                                </div>
                                            ))}
                                    </div>

                                    {/* Selected Products */}
                                    <div className="multi-select-list">
                                        <strong>Selected</strong>
                                        {editForm.ORG_PERMISSIONS.filter(p => p.Category === "Product").map(p => (
                                            <div
                                                key={p.App}
                                                className="multi-select-item"
                                                onClick={() =>
                                                    handleEditChange(
                                                        "ORG_PERMISSIONS",
                                                        editForm.ORG_PERMISSIONS.filter(x => x.App !== p.App)
                                                    )
                                                }
                                            >
                                                {p.App} ×
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </label>

                            <label className="full-width-section">
                                <strong>Default Permissions:</strong>
                                <div className="multi-select-container">
                                    {/* Available Permissions */}
                                    <div className="multi-select-list">
                                        <strong>Available</strong>
                                        {availablePermissions
                                            .filter(p => p.Category === "Permission" && !editForm.ORG_PERMISSIONS.some(sel => sel.App === p.App))
                                            .map(p => (
                                                <div
                                                    key={p.App}
                                                    className="multi-select-item"
                                                    onClick={() =>
                                                        handleEditChange("ORG_PERMISSIONS", [...editForm.ORG_PERMISSIONS, p])
                                                    }
                                                >
                                                    {p.App} +
                                                </div>
                                            ))}
                                    </div>

                                    {/* Selected Permissions */}
                                    <div className="multi-select-list">
                                        <strong>Selected</strong>
                                        {editForm.ORG_PERMISSIONS.filter(p => p.Category === "Permission").map(p => (
                                            <div
                                                key={p.App}
                                                className="multi-select-item"
                                                onClick={() =>
                                                    handleEditChange(
                                                        "ORG_PERMISSIONS",
                                                        editForm.ORG_PERMISSIONS.filter(x => x.App !== p.App)
                                                    )
                                                }
                                            >
                                                {p.App} ×
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </label>

                            {/* ORG_LOCATIONS */}
                            <label className="full-width-section">
                                <strong>Regions:</strong>
                                <div className="multi-select-container">
                                    {/* Available */}
                                    <div className="multi-select-list">
                                        <strong>Available</strong>
                                        {availableLocations
                                            .filter(l => !editForm.ORG_LOCATIONS.includes(l))
                                            .map(l => (
                                                <div
                                                    key={l}
                                                    className="multi-select-item"
                                                    onClick={() =>
                                                        handleEditChange("ORG_LOCATIONS", [...editForm.ORG_LOCATIONS, l])
                                                    }
                                                >
                                                    {formatRegionName(l)} +
                                                </div>
                                            ))}
                                    </div>

                                    {/* Selected */}
                                    <div className="multi-select-list">
                                        <strong>Selected</strong>
                                        {editForm.ORG_LOCATIONS.map(l => (
                                            <div
                                                key={l}
                                                className="multi-select-item"
                                                onClick={() =>
                                                    handleEditChange(
                                                        "ORG_LOCATIONS",
                                                        editForm.ORG_LOCATIONS.filter(x => x !== l)
                                                    )
                                                }
                                            >
                                                {formatRegionName(l)} ×
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </label>


                            {/* USERS */}
                            {/* <div className="edit-org-users">
                                <strong>Members:</strong>
                                {editForm.users.map((user, index) => (
                                    <div key={user.user_id} className="edit-user">
                                        <input
                                            type="text"
                                            placeholder="First Name"
                                            value={user.first_name}
                                            onChange={(e) => {
                                                const updatedUsers = [...editForm.users];
                                                updatedUsers[index] = { ...user, first_name: e.target.value };
                                                handleEditChange("users", updatedUsers);
                                            }}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Last Name"
                                            value={user.last_name}
                                            onChange={(e) => {
                                                const updatedUsers = [...editForm.users];
                                                updatedUsers[index] = { ...user, last_name: e.target.value };
                                                handleEditChange("users", updatedUsers);
                                            }}
                                        />
                                        <input
                                            type="email"
                                            placeholder="Email"
                                            value={user.email}
                                            onChange={(e) => {
                                                const updatedUsers = [...editForm.users];
                                                updatedUsers[index] = { ...user, email: e.target.value };
                                                handleEditChange("users", updatedUsers);
                                            }}
                                        />
                                    </div>
                                ))}
                            </div> */}
                        </div>

                        {/* Actions */}
                        <div className="hh-duo hh-actions edit-org-popup-footer">
                            <button className="cancel-btn"
                                onClick={() => {
                                    setActiveSection("");
                                    setOrgBeingEdited(null);
                                    setEditForm(null);
                                    setSelectedFile(null)
                                }}
                            >
                                Cancel</button>
                            <button className="confirm-btn" onClick={saveOrg}>
                                {activeSection === "CreateOrg" ? "Create Customer" : "Save Changes"}
                            </button>


                        </div>

                    </div>
                </div>
            )}

            {/* Delete confirmation popup */}
            {showDeleteOrgPopup && orgBeingEdited && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content hh-del-pop">
                        <div className="hh-delete-inner-pop" style={{ gap: "2rem" }}>

                            <div className="hh-delete-text-and-icon">


                                <div className="marketing-finish-icon-div">
                                    <CgTrash className="marketing-finish-icon" />
                                </div>

                                <div>
                                    <h3>Delete Organization</h3>

                                    <p>
                                        Are you sure you want to permanently delete this organization? Users
                                        from a deleted organization will have all their permissions restricted.
                                    </p>

                                    <p style={{ marginTop: "1rem" }}>
                                        <strong>Name:</strong> {orgBeingEdited.ORG_NAME}<br />
                                        <strong>Plan:</strong> {orgBeingEdited.ORG_PLAN}<br />
                                        <strong>Industry:</strong> {orgBeingEdited.ORG_INDUSTRY}
                                    </p>

                                    <p style={{ marginTop: "1rem", color: "#d92d20" }}>
                                        This action cannot be undone.
                                    </p>
                                </div>
                            </div>

                            <div className="hh-duo hh-actions">
                                <button
                                    className="cancel-btn"
                                    onClick={() => setShowDeleteOrgPopup(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="hh-delete-btn"
                                    onClick={async () => {
                                        setShowDeleteOrgPopup(false);
                                        await handleDeleteOrg();
                                    }}
                                >
                                    Delete Organization
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}




        </div>
    );
};

export default CustomerDescription;
